import { Injectable } from '@angular/core';
import { WatchQueryFetchPolicy } from '@apollo/client/core';
import { Apollo } from 'apollo-angular';
import { ReplaySubject, combineLatest } from 'rxjs';
import ALERT_MESSAGE_QUERY from '../apollo/queries/collections/alertmessage';
import { PREFETCH_QUERY } from '../apollo/queries/collections/article';
import { CAMPAIGNS_QUERY, Campaign } from '../apollo/queries/collections/campaigns';

export type AlertMessage = {
  title: string;
  short_description: string;
  full_description: string;
  variant: string;
}

@Injectable({
  providedIn: 'root'
})
export class ContentService {

  currentLocale$ = new ReplaySubject<string>(1);
  articleUrlMap$ = new ReplaySubject<{ [url: string]: string }>(1);
  alertMessagesMap: { [locator: string]: AlertMessage } = {};
  campaigns$ = new ReplaySubject<Campaign[]>(1);

  constructor(
    private apollo: Apollo,
  ) {
    this.currentLocale$.next(localStorage.getItem("locale") || "en");
  }

  public getLocale() {
    return this.currentLocale$;
  }

  public setLocale(locale: string) {
    const currentLocale = localStorage.getItem("locale");
    if (currentLocale !== locale) {
      localStorage.setItem("locale", locale);
      this.currentLocale$.next(locale);
    }
  }

  public getContent(query: any, variables?: any, fetchPolicy: WatchQueryFetchPolicy = 'cache-and-network') {
    return this.apollo.watchQuery<any>({
      query,
      fetchPolicy,
      variables: {
        locale: 'en',
        ...variables,
      },
    }).valueChanges;
  }

  async prefetchArticles() {
    this.apollo.watchQuery<any>({
      query: PREFETCH_QUERY,
      fetchPolicy: 'cache-and-network',
      variables: {}
    }).valueChanges.subscribe((data) => {
      if (data.data.articles.data.length) {
        const result: { [id: string]: string } = {};
        data.data.articles.data.forEach((res: any) => {
          if (res.attributes?.url && res.id) {
            result[res.attributes.url] = res.id;
          }
        });

        this.articleUrlMap$.next(result);
      }
      return this.apollo.client.writeQuery({
        query: PREFETCH_QUERY,
        data,
      });
    });
  }

  async prefetchAlertMessages() {
    combineLatest([
      this.apollo.watchQuery<any>({
        query: ALERT_MESSAGE_QUERY,
        fetchPolicy: 'cache-and-network',
        variables: {
          locale: 'en',
        }
      }).valueChanges,
      this.currentLocale$,
    ]).subscribe(([data, locale]) => {
      if (data?.data?.alertMessages?.data?.length) {
        let _alertMessagesMap: { [locator: string]: AlertMessage } = {};
        data.data.alertMessages.data.forEach((entry: any) => {
          if (entry.attributes?.locator) {
            _alertMessagesMap[entry.attributes.locator] = {
              full_description: entry.attributes.full_description || '',
              short_description: entry.attributes.short_description || '',
              title: entry.attributes.title || '',
              variant: entry.attributes.variant || '',
            };
            const localizedAlertMessage = entry.attributes?.localizations?.data?.find((alertMsg: any) => alertMsg.attributes?.locale === locale);
            if (localizedAlertMessage) {
              _alertMessagesMap[entry.attributes.locator].full_description = localizedAlertMessage.attributes.full_description || _alertMessagesMap[entry.attributes.locator].full_description;
              _alertMessagesMap[entry.attributes.locator].short_description = localizedAlertMessage.attributes.short_description || _alertMessagesMap[entry.attributes.locator].short_description;
              _alertMessagesMap[entry.attributes.locator].title = localizedAlertMessage.attributes.title || _alertMessagesMap[entry.attributes.locator].title;
              _alertMessagesMap[entry.attributes.locator].variant = localizedAlertMessage.attributes.variant || _alertMessagesMap[entry.attributes.locator].variant;
            }
          }
        });
        Object.assign(this.alertMessagesMap, _alertMessagesMap);
      }
    });
  }

  async prefetchCampaigns() {
    this.apollo.watchQuery<any>({
      query: CAMPAIGNS_QUERY,
      fetchPolicy: 'cache-and-network',
      variables: {
        locale: 'en',
      }
    }).valueChanges.subscribe((campaignsResult) => {
      if (campaignsResult?.data?.campaigns?.data) {
        const campaigns: Campaign[] = (campaignsResult.data.campaigns.data as Campaign[]).filter(c => c.attributes.product?.data?.attributes?.identifier === 'app');
        this.campaigns$.next(campaigns);
      }
    });
  }

}
